<!-- ----------------------------------------------- -->
<template>
  <v-card light class="calender-content com-popup">
    <v-row class="com-popup-header">
      <span class="com-popup-title">{{ text.calendarTitle }}</span>
      <v-spacer></v-spacer>
      <v-btn icon x-small @click="closeCalendar">
        <v-icon color="white">highlight_off</v-icon>
      </v-btn>
    </v-row>
    <v-container>
      <v-row align="center">
        <v-col cols="1" class="pt-0 pb-0">
          <v-icon>event</v-icon>
        </v-col>
        <v-col cols="5" class="pt-0 pb-0">
          <span>日付</span>
        </v-col>
        <v-col cols="1" class="pt-0 pb-0">
          <v-icon>access_time</v-icon>
        </v-col>
        <v-col cols="5" class="pt-0 pb-0">
          <span>時刻</span>
        </v-col>
      </v-row>
      <v-row align="center">
        <v-col cols="6" class="pt-0 pb-0">
          <v-select v-model="date" :items="dateList" class="pt-0"></v-select>
        </v-col>
        <v-col cols="3" class="pt-0 pb-0">
          <v-select v-model="hour" :items="hourList" class="pt-0"></v-select>
        </v-col>
        <v-col cols="3" class="pt-0 pb-0">
          <v-select v-model="min" :items="minList" class="pt-0"></v-select>
        </v-col>
      </v-row>
      <v-row align="center" justify="end" class="pr-2">
        <v-btn outlined small color="success" @click="commit">
          <span>{{ text.commit }}</span>
        </v-btn>
      </v-row>
    </v-container>
  </v-card>
</template>
<!-- ----------------------------------------------- -->
<script>
/**
 * 川の防災情報 地図画面 日付入力ダイアログ
 * Calendar.vue
 * @author LIS
 */
import mixin from "./../../lib/mixin.js";
import Util from "./../../lib/util.js";
import PageLog from "../../lib/pageLog.js";

export default {
  name: "map-calendar",
  mixins: [mixin],
  data() {
    return {
      /** 文字列リソース */
      text: {
        calendarTitle: "過去日付選択",
        commit: "設定",
      },
      /** 選択日付 */
      date: null,
      /** 選択時 */
      hour: null,
      /** 選択分 */
      min: null,
    };
  },
  computed: {
    /** 設定情報 */
    config: {
      get() {
        return this.$store.state.app.config;
      },
    },
    /** 観測日時 */
    getAppTime: {
      get() {
        return this.$store.state.app.appTime;
      },
    },
    /** 日付入力ダイアログ表示状態 */
    visibility: {
      get() {
        return this.$store.state.map.showCalendar;
      },
    },
    /** 履歴再生期間 */
    historyDays: {
      get() {
        return this.config.appConfig.calendar.historyDays;
      },
    },
    /** レーダ雨量の種類 */
    radarType: {
      get() {
        return this.$store.state.radar.radarType;
      },
    },
    /** レーダ観測日時 */
    radarDate: {
      get() {
        if (this.$store.state.radar.radarType == "xrain") {
          return Util.toDate(this.$store.state.radar.radarXrainDate);
        }
        return Util.toDate(this.$store.state.radar.radarCbandDate);
      },
    },
    /** レーダ最新観測日時 */
    radarNewDate: {
      get() {
        if (this.$store.state.radar.radarType == "xrain") {
          return Util.toDate(this.$store.state.radar.radarNewXrainDate);
        }
        return Util.toDate(this.$store.state.radar.radarNewCbandDate);
      },
    },
    /** 日付選択リスト */
    dateList: {
      get() {
        if (this.visibility === true) {
          let items = [];
          // 最新観測日時より未来は指定できないようにするため、ラウンドした日時を使用
          let currentDate = Util.roundMinutes(
            Util.toDate(),
            this.config.appConfig.keepDaysJsonFiles,
            this.radarNewDate
          );
          for (let i = 0; i < this.historyDays + 1; i++) {
            items.push(Util.formatDate24(currentDate, "YYYY/MM/DD"));
            currentDate = Util.subDays(currentDate, 1);
          }
          return items;
        }
        return [];
      },
    },
    /** 時選択リスト */
    hourList: {
      get() {
        if (this.visibility === true) {
          let items = [];
          // 最新観測日時より未来は指定できないようにするため、ラウンドした日時を使用
          let currentHour = Util.formatDate24(
            Util.roundMinutes(
              Util.toDate(),
              this.config.appConfig.keepDaysJsonFiles,
              this.radarNewDate
            ),
            "HH"
          );
          let h;

          // 履歴再生期間よりも過去の「時間」は選択不可とする
          if (this.date === this.dateList[this.historyDays]) {
            for (h = currentHour; h <= 24; h++) {
              items.push(("0" + h).slice(-2));
            }
          }
          // 履歴再生期間よりも未来の「時間」は選択不可とする
          else if (this.date === this.dateList[0]) {
            for (h = 0; h <= currentHour; h++) {
              items.push(("0" + h).slice(-2));
            }
          } else {
            for (h = 0; h <= 24; h++) {
              items.push(("0" + h).slice(-2));
            }
          }
          return items;
        }
        return [];
      },
    },
    /** 分選択リスト */
    minList: {
      get() {
        if (this.visibility === true) {
          // 最新観測日時より未来は指定できないようにするため、ラウンドした日時を使用
          const roundNowDate = Util.roundMinutes(
            Util.toDate(),
            this.config.appConfig.keepDaysJsonFiles,
            this.radarNewDate
          );
          let currentHour = Util.formatDate24(roundNowDate, "HH");
          let currentMin = Util.formatDate24(roundNowDate, "mm");

          if (this.hour === "24") {
            // 24時の場合は00分のみ
            return ["00"];
          } else {
            let items = [];
            // 履歴再生期間よりも過去の「分」は選択不可とする
            if (
              this.date === this.dateList[this.historyDays] &&
              this.hour === currentHour
            ) {
              items = this.getMinList(currentMin, 55);
            }
            // 履歴再生期間よりも未来の「分」は選択不可とする
            else if (
              this.date === this.dateList[0] &&
              this.hour === currentHour
            ) {
              items = this.getMinList(0, currentMin);
            } else {
              items = this.getMinList(0, 55);
            }
            // 00時の場合は、00分を除く
            if (this.hour === "00") {
              return items.filter((n) => n !== "00");
            }
            return items;
          }
        }
        return [];
      },
    },
  },
  methods: {
    /** 観測日時指定ダイアログの非表示 */
    closeCalendar() {
      this.$store.commit("map/setShowCalendar", false);
    },
    /** 設定 */
    commit() {
      // アクセスログ出力
      PageLog.log({
        page_log: "map_time",
        device_sb: this.$store.state.app.mode,
        fc_flg: this.$store.getters["app/isFldCtl"],
      });

      // レーダの履歴動画再生状態をリセット
      this.$store.commit("radar/resetHistoryPlay");
      // 過去画像動画状態をリセット
      this.$store.commit("Telemeter/resetHistoryPlay");
      this.$store.commit("Telemeter/resetHistoryPrintCurrentPicture");
      // ストアの観測日時更新
      let selectedDate = Util.addDate(this.date, 0, this.hour, this.min);
      this.$store.commit("app/setTime", selectedDate);
      this.$store.commit("map/setShowCalendar", false);
      // 画面遷移時の保持日時を更新
      this.$store.commit("app/setRetentionTime", {
        time: selectedDate,
        flag: false,
      });
    },
    /** 分のリストを返す */
    getMinList(startMin, endMin) {
      let items = [];
      // 5分間隔期間の対象かを判定
      if (
        Util.checkShortInterval(
          Util.toDate(this.date),
          this.config.appConfig.keepDaysJsonFiles
        )
      ) {
        for (let m = Math.floor(startMin / 5) * 5; m <= endMin; m += 5) {
          items.push(("0" + m).slice(-2));
        }
      } else {
        // 5分間隔期間の対象外の場合は、10分間隔を設定
        for (let m = Math.floor(startMin / 10) * 10; m <= endMin; m += 10) {
          items.push(("0" + m).slice(-2));
        }
      }
      return items;
    },
  },
  watch: {
    /** 日付入力ダイアログ表示状態監視 */
    visibility() {
      if (this.visibility) {
        this.date = Util.formatDate24(this.radarDate, "YYYY/MM/DD");
        this.hour = Util.formatDate24(this.radarDate, "HH");
        this.min = Util.formatDate24(this.radarDate, "mm");
        if (
          Util.checkShortInterval(
            this.radarDate,
            this.config.appConfig.keepDaysJsonFiles
          )
        ) {
          this.min = ("0" + (this.min - (this.min % 5))).slice(-2);
        } else {
          this.min = ("0" + (this.min - (this.min % 10))).slice(-2);
        }

        // リスト更新により存在しない値となった場合、先頭を選択する
        const hit = this.dateList.find((date) => date == this.date);
        if (!hit) {
          this.date = this.dateList[0];
        }
      }
    },
    /** 時選択リスト監視 */
    hourList() {
      if (this.hourList) {
        // リスト更新により存在しない値となった場合、先頭を選択する
        const hit = this.hourList.find((hour) => hour == this.hour);
        if (!hit) {
          this.hour = this.hourList[0];
        }
      }
    },
    /** 分選択リスト監視 */
    minList() {
      if (this.minList) {
        // リスト更新により存在しない値となった場合、先頭を選択する
        const hit = this.minList.find((min) => min == this.min);
        if (!hit) {
          this.min = this.minList[0];
        }
      }
    },
    /** 観測日時 */
    getAppTime() {
      this.$store.commit("map/setShowCalendar", false);
    },
    /** レーダ雨量の種類 */
    radarType() {
      this.$store.commit("map/setShowCalendar", false);
    },
  },
};
</script>
<!-- ----------------------------------------------- -->
<style lang="scss" scoped>
.calender-content {
  position: absolute;
  z-index: 3045;
  top: 4px;
  right: 4px;
  width: 350px;
  max-width: calc(100% - 8px);
}
</style>
